import React, { useMemo, useState } from "react";
import { BANK_ACCOUNT_TYPE_OPTIONS } from "../../../../constants/accountConstants";
import {
  canonicalBankAccountType,
  formatBankLabel,
  getAccountBanks,
  getUnlinkedHouseholdBanks,
  resolveAccountBankIssues,
  filterBankIssuesByReason,
} from "../../../../utils/bankAccounts";
import { formatRoutingNumberInput } from "../../../../utils/validationHelpers";

const isMissing = (val) => val === null || val === undefined || val === "";

const emptyNewBank = () => ({
  bank_name: "",
  account_name: "",
  account_number: "",
  routing_number: "",
  account_type: "",
  is_personal: true,
});

/**
 * Stacked-card form for completing missing fields across multiple bank accounts
 * linked to a single investment account.
 *
 * When the backend reports "bank_details" is missing for an account, there are
 * two shapes the UI has to handle:
 *
 *  1. Account has one or more linked bank accounts that are missing required
 *     fields — render a card per incomplete bank and emit
 *     `bank_accounts: { update: [...] }`.
 *  2. Account has zero linked bank accounts — the account has a withdrawal or
 *     deposit but no destination bank yet. Offer to create a new bank or link
 *     an existing household bank and emit `bank_accounts: { create | link }`.
 */
const BankDetailsForm = ({
  householdData,
  fieldData,
  entityId,
  onSave,
  onSaveAndExit,
  saving,
  nextLabel,
}) => {
  const account = fieldData || {};

  const incompleteBanks = useMemo(
    () =>
      filterBankIssuesByReason(
        resolveAccountBankIssues(account, householdData?.missing_fields, householdData),
        "missing_data",
      ),
    [account, householdData],
  );

  const linkedBanksCount = useMemo(
    () => getAccountBanks(account).length,
    [account],
  );

  const availableBanks = useMemo(
    () => getUnlinkedHouseholdBanks(householdData, account),
    [householdData, account],
  );

  const needsNewBank = linkedBanksCount === 0 && incompleteBanks.length === 0;

  const [mode, setMode] = useState(() =>
    needsNewBank && availableBanks.length > 0 ? "link" : "create",
  );
  const [newBank, setNewBank] = useState(emptyNewBank);
  const [linkBankId, setLinkBankId] = useState(
    availableBanks[0]?.id || "",
  );

  const [formByBank, setFormByBank] = useState(() => {
    const state = {};
    incompleteBanks.forEach(({ bank }) => {
      if (!bank?.id) return;
      state[bank.id] = {
        bank_name: bank.bank_name || "",
        account_name: bank.account_name || "",
        account_number: bank.account_number || "",
        routing_number: bank.routing_number || "",
        account_type: canonicalBankAccountType(bank.account_type),
        is_personal: bank.is_personal ?? true,
      };
    });
    return state;
  });

  const handleChange = (bankId, field, value) => {
    setFormByBank((prev) => ({
      ...prev,
      [bankId]: { ...(prev[bankId] || {}), [field]: value },
    }));
  };

  const handleNewBankChange = (field, value) => {
    setNewBank((prev) => ({ ...prev, [field]: value }));
  };

  const totalMissing = incompleteBanks.reduce(
    (acc, { missingKeys }) => acc + (missingKeys?.length || 0),
    0,
  );

  const buildUpdatePayload = () => {
    const updates = [];
    incompleteBanks.forEach(({ bank }) => {
      if (!bank?.id) return;
      const draft = formByBank[bank.id] || {};
      const diff = { id: bank.id };
      let changed = false;

      ["bank_name", "account_name", "account_number", "routing_number", "account_type"].forEach(
        (key) => {
          const next = typeof draft[key] === "string" ? draft[key].trim() : draft[key];
          const nextOrNull = next === "" ? null : next ?? null;
          const current = bank[key] ?? null;
          if (nextOrNull !== current) {
            diff[key] = nextOrNull;
            changed = true;
          }
        },
      );

      const nextPersonal = draft.is_personal;
      if (nextPersonal !== bank.is_personal) {
        diff.is_personal = nextPersonal;
        changed = true;
      }

      if (changed) updates.push(diff);
    });

    if (updates.length === 0) {
      return { accounts: { [entityId]: {} } };
    }
    return {
      accounts: {
        [entityId]: { bank_accounts: { update: updates } },
      },
    };
  };

  const buildAddPayload = () => {
    if (mode === "link") {
      if (!linkBankId) return { accounts: { [entityId]: {} } };
      return {
        accounts: {
          [entityId]: { bank_accounts: { link: [linkBankId] } },
        },
      };
    }

    // mode === "create"
    const trimmed = {};
    ["bank_name", "account_name", "account_number", "routing_number", "account_type"].forEach(
      (key) => {
        const raw = newBank[key];
        const next = typeof raw === "string" ? raw.trim() : raw;
        trimmed[key] = next === "" ? null : (next ?? null);
      },
    );
    trimmed.is_personal = newBank.is_personal ?? true;
    return {
      accounts: {
        [entityId]: { bank_accounts: { create: [trimmed] } },
      },
    };
  };

  const buildPayload = () => {
    if (needsNewBank) return buildAddPayload();
    return buildUpdatePayload();
  };

  const handleSubmit = (cb) => cb(buildPayload());

  const isCreateDisabled =
    mode === "create" &&
    (isMissing(newBank.bank_name) ||
      isMissing(newBank.account_name) ||
      isMissing(newBank.account_number) ||
      isMissing(newBank.routing_number) ||
      isMissing(newBank.account_type));
  const isLinkDisabled = mode === "link" && !linkBankId;
  const submitDisabled = saving || (needsNewBank && (isCreateDisabled || isLinkDisabled));

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Bank Accounts</h2>
          {needsNewBank ? (
            <p className="text-xs text-gray-500 mt-0.5">
              This account has a withdrawal or deposit but no linked bank account.
              Add one below.
            </p>
          ) : (
            <p className="text-xs text-gray-500 mt-0.5">
              {incompleteBanks.length} bank account
              {incompleteBanks.length !== 1 ? "s" : ""} need
              {incompleteBanks.length === 1 ? "s" : ""} attention
            </p>
          )}
        </div>
        {!needsNewBank && totalMissing > 0 && (
          <span className="text-sm text-red-500 font-medium">
            ● {totalMissing} field{totalMissing !== 1 ? "s" : ""} missing
          </span>
        )}
      </div>

      {needsNewBank ? (
        <section className="border border-gray-200 rounded-xl p-5 bg-white">
          {availableBanks.length > 0 && (
            <div className="flex bg-gray-100 rounded-lg p-1 w-full md:w-96 mb-5">
              <button
                type="button"
                onClick={() => setMode("link")}
                className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                  mode === "link"
                    ? "bg-white text-emerald-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                disabled={saving}
              >
                Link existing
              </button>
              <button
                type="button"
                onClick={() => setMode("create")}
                className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                  mode === "create"
                    ? "bg-white text-emerald-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
                disabled={saving}
              >
                Add new
              </button>
            </div>
          )}

          {mode === "link" && availableBanks.length > 0 ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select a bank account from this household{" "}
                <span className="text-red-500">*</span>
              </label>
              <select
                value={linkBankId}
                onChange={(e) => setLinkBankId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                disabled={saving}
              >
                {availableBanks.map((ba) => (
                  <option key={ba.id} value={ba.id}>
                    {formatBankLabel(ba)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                This bank account will be linked to the investment account. No
                fields will be changed.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
              <FormField
                label="Bank Name"
                required
                value={newBank.bank_name}
                onChange={(v) => handleNewBankChange("bank_name", v)}
                placeholder="Enter bank name"
              />
              <FormField
                label="Account Name"
                required
                value={newBank.account_name}
                onChange={(v) => handleNewBankChange("account_name", v)}
                placeholder="Enter account name"
              />
              <FormField
                label="Account Number"
                required
                value={newBank.account_number}
                onChange={(v) => handleNewBankChange("account_number", v)}
                placeholder="Enter account number"
              />
              <FormField
                label="Routing Number"
                required
                value={newBank.routing_number}
                onChange={(v) => handleNewBankChange("routing_number", v)}
                placeholder="Enter routing number (9 digits)"
                maxLength={9}
                inputMode="numeric"
                format={formatRoutingNumberInput}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={newBank.account_type || ""}
                  onChange={(e) =>
                    handleNewBankChange("account_type", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  <option value="">Select Account Type</option>
                  {BANK_ACCOUNT_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.label}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Category <span className="text-red-500">*</span>
                </label>
                <div className="flex bg-gray-100 rounded-lg p-1 w-full md:w-64">
                  <button
                    type="button"
                    onClick={() => handleNewBankChange("is_personal", true)}
                    className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                      newBank.is_personal
                        ? "bg-white text-emerald-700 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                    disabled={saving}
                  >
                    Personal
                  </button>
                  <button
                    type="button"
                    onClick={() => handleNewBankChange("is_personal", false)}
                    className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                      !newBank.is_personal
                        ? "bg-white text-emerald-700 shadow-sm"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                    disabled={saving}
                  >
                    Business
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      ) : incompleteBanks.length === 0 ? (
        <p className="text-sm text-gray-500">
          No bank accounts require additional information for this account.
        </p>
      ) : (
        <div className="space-y-5">
          {incompleteBanks.map(({ bank, missingKeys }, idx) => {
            const form = formByBank[bank.id] || {};
            const missingSet = new Set(missingKeys || []);

            return (
              <section
                key={bank.id}
                className="border border-gray-200 rounded-xl p-5 bg-white"
              >
                <header className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                      {idx + 1}
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">
                        {formatBankLabel(bank)}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {missingKeys.length > 0
                          ? `${missingKeys.length} field${missingKeys.length !== 1 ? "s" : ""} missing`
                          : "All fields present"}
                      </p>
                    </div>
                  </div>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                  <FormField
                    label="Bank Name"
                    required={missingSet.has("bank_name") || isMissing(bank.bank_name)}
                    value={form.bank_name}
                    onChange={(v) => handleChange(bank.id, "bank_name", v)}
                    placeholder="Enter bank name"
                  />
                  <FormField
                    label="Account Name"
                    required={missingSet.has("account_name") || isMissing(bank.account_name)}
                    value={form.account_name}
                    onChange={(v) => handleChange(bank.id, "account_name", v)}
                    placeholder="Enter account name"
                  />
                  <FormField
                    label="Account Number"
                    required={missingSet.has("account_number") || isMissing(bank.account_number)}
                    value={form.account_number}
                    onChange={(v) => handleChange(bank.id, "account_number", v)}
                    placeholder="Enter account number"
                  />
                  <FormField
                    label="Routing Number"
                    required={missingSet.has("routing_number") || isMissing(bank.routing_number)}
                    value={form.routing_number}
                    onChange={(v) => handleChange(bank.id, "routing_number", v)}
                    placeholder="Enter routing number (9 digits)"
                    maxLength={9}
                    inputMode="numeric"
                    format={formatRoutingNumberInput}
                  />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Type {(missingSet.has("account_type") || isMissing(bank.account_type)) && <span className="text-red-500">*</span>}
                    </label>
                    <select
                      value={form.account_type || ""}
                      onChange={(e) => handleChange(bank.id, "account_type", e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    >
                      <option value="">Select Account Type</option>
                      {BANK_ACCOUNT_TYPE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.label}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Account Category {(missingSet.has("is_personal") || bank.is_personal === null || bank.is_personal === undefined) && <span className="text-red-500">*</span>}
                    </label>
                    <div className="flex bg-gray-100 rounded-lg p-1 w-full md:w-64">
                      <button
                        type="button"
                        onClick={() => handleChange(bank.id, "is_personal", true)}
                        className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${form.is_personal ? "bg-white text-emerald-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                        disabled={saving}
                      >
                        Personal
                      </button>
                      <button
                        type="button"
                        onClick={() => handleChange(bank.id, "is_personal", false)}
                        className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${!form.is_personal ? "bg-white text-emerald-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                        disabled={saving}
                      >
                        Business
                      </button>
                    </div>
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button
          onClick={() => handleSubmit(onSaveAndExit)}
          disabled={submitDisabled}
          className="px-6 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
        >
          Save & Exit
        </button>
        <button
          onClick={() => handleSubmit(onSave)}
          disabled={submitDisabled}
          className="px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {saving && (
            <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          {nextLabel || "Save & Next →"}
        </button>
      </div>
    </div>
  );
};

const FormField = ({
  label,
  required,
  value,
  onChange,
  placeholder,
  maxLength,
  inputMode,
  format,
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <input
      type="text"
      value={value || ""}
      onChange={(e) => {
        const raw = e.target.value;
        const next = typeof format === "function" ? format(raw) : raw;
        onChange(next);
      }}
      placeholder={placeholder}
      maxLength={maxLength || undefined}
      inputMode={inputMode || undefined}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
    />
  </div>
);

export default BankDetailsForm;
